import { fetchVideoGenerateList, type VideoGenerateRecord } from "../librarySlice";
import { useAppDispatch, useAppSelector } from "@hooks/redux";
import { useState } from "react";
import videoCreatingService from "@apis/videoCreatingService";
import { fetchAiCredits } from "@store/globalSlice";
import { Status } from "@models/common";
import { message } from "antd";
import { t } from "i18next";
import { useMembershipLevel } from "@hooks/membership";
import { getPreviewVideoCredit } from "@apis/http/api";
import { isNil } from "@kalodata/tools";

// 计算卡片的数据
// export const usePageSize = () => {
//   const refCardWrap = useRef<HTMLDivElement>(null);
//   const dispatch = useAppDispatch();

//   const setCardPageSize = () => {
//     const wrapWidth = refCardWrap.current?.getBoundingClientRect()?.width;
//     if (wrapWidth) {
//       const pageSize = Math.floor((wrapWidth + 16) / (256 + 16));
//       dispatch(updateCardPageSize(pageSize * 2));
//     } else {
//       dispatch(updateCardPageSize(4 * 2));
//     }
//   };

//   useEffect(() => {
//     setCardPageSize();
//   }, []);

//   useEffect(() => {
//     window.addEventListener("resize", setCardPageSize);
//     return () => {
//       window.removeEventListener("resize", setCardPageSize);
//     };
//   }, []);

//   return { refCardWrap };
// };

const retryCostCache = new Map<string, number>();

function buildCostCacheKey(ctx: VideoGenerateRecord["jobItem"]["context"], internalModelId: string) {
  return `${ctx.aspectRatio}_${ctx.duration}_${ctx.quantity || 1}_${ctx.resolution}_${internalModelId}`;
}

export const useVideoGenerateCard = (record: VideoGenerateRecord) => {
  const [retryCost, setRetryCost] = useState<number | null>(null);
  const [retryStatus, setRetryStatus] = useState<Status>("idle");
  const { totalRemain } = useAppSelector((state) => state.globalReducer.creditInfo);
  const dispatch = useAppDispatch();
  const { isBasicUp } = useMembershipLevel();

  const context = record?.jobItem?.context;
  const internalModelId = record?.jobItem?.internalModelId || "";
  const { aspectRatio, duration, quantity, resolution } = context || {};
  const jobId = record?.jobItem?.jobId;

  const fetchRetryCost = async (): Promise<number> => {
    const cacheKey = buildCostCacheKey(context, internalModelId);
    const cached = retryCostCache.get(cacheKey);
    if (cached !== undefined) return cached;

    const res = await getPreviewVideoCredit({
      aspectRatio,
      duration,
      quantity,
      resolution,
      internalModelId,
    } as unknown as any);
    const cost: number = res || 0;
    retryCostCache.set(cacheKey, cost);
    return cost;
  };

  const handleRetryHoverChange = async (open: boolean) => {
    if (!open || retryCost !== null) return;
    if (isNil(internalModelId) || !internalModelId) {
      return;
    }
    try {
      const cost = await fetchRetryCost();
      setRetryCost(cost);
    } catch {
      setRetryCost(0);
    }
  };

  const handleVideoRecreation = async () => {
    if (!jobId || retryStatus === "loading") return;
    if (isNil(internalModelId) || !internalModelId) {
      message.error(t("This model is unavailable. Unable to regenerate."));
      return;
    }
    if (retryCost === null) {
      try {
        const cost = await fetchRetryCost();
        setRetryCost(cost);
      } catch {
        message.error(t("Failed to check credits"));
        return;
      }
    }

    if (retryCost && totalRemain < retryCost) return;

    setRetryStatus("loading");
    try {
      await videoCreatingService.videoRecreation({ jobId, internalModelId });
      setRetryStatus("succeeded");
      message.success(t("Retry submitted successfully"));
      dispatch(fetchVideoGenerateList({}));
      dispatch(fetchAiCredits());
    } catch {
      setRetryStatus("failed");
      message.error(t("Retry failed"));
    }
  };

  return {
    retryCost,
    totalRemain,
    isBasicUp,
    handleRetryHoverChange,
    handleVideoRecreation,
  };
};
